// @ts-nocheck
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '@way/theme-host/services/logger.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PackageService } from '@way/theme-host/services/package.service';
import { NotificationService } from '../../../../app/services/notification.service'; // NotificationService;
import { Package } from '@way/theme-host/shared/types';
import {
  ModernPageHeaderComponent as PageHeaderComponent,
  NavigationItem,
  Logo,
  CTAButton,
} from '../shared/page-header/page-header.component';
import { buildHeaderCtaButton } from '../shared/header-cta.util';
import { ModernPageFooterComponent as PageFooterComponent } from '../shared/footer/footer.component';
import { resolveFooterQuickLinks } from '@way/theme-host/shared/utils/footer-quick-links.util';

export interface WebsiteElement {
  id: string;
  type:
    | 'header'
    | 'hero'
    | 'section'
    | 'testimonials'
    | 'services'
    | 'booking'
    | 'contact'
    | 'footer'
    | 'fleets'
    | 'packages'
    | 'coupons'
    | 'express-details';
  content: any;
  selected?: boolean;
}

interface FleetFormData {
  company_name: string;
  contact_name: string;
  phone: string;
  email: string;
  wash_type: string;
  vehicle_count: number | null;
  notes: string;
}

@Component({
  selector: 'app-modern-fleet-management-preview',
  standalone: true,
  imports: [CommonModule, FormsModule, PageHeaderComponent, PageFooterComponent],
  providers: [NotificationService],
  templateUrl: './fleet-management-preview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./fleet-management-preview.component.scss'],
})
export class ModernFleetManagementPreviewComponent implements OnInit, OnChanges {
  @Input() pageData: any = null;
  @Input() sectionVisibility: Record<string, boolean> = {};
  @Input() pages: any[] = [];
  @Input() currentPageType = '';
  @Input() elements: WebsiteElement[] = [];
  @Input() deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  @Input() primaryColor: string | null = null; // Primary color from theme
  @Input() headingColor: string | null = null; // Heading color from branding
  @Input() paragraphColor: string | null = null; // Paragraph/description color from branding
  @Input() headingFont: string | null = null; // Heading font from branding
  @Input() bodyFont: string | null = null; // Body font from branding
  @Output() elementSelect = new EventEmitter<WebsiteElement>();
  @Output() navigationClick = new EventEmitter<string>();

  packages: Package[] = [];
  loading = false;
  submitting = false;

  // Form data
  formData: FleetFormData = {
    company_name: '',
    contact_name: '',
    phone: '',
    email: '',
    wash_type: '',
    vehicle_count: null,
    notes: '',
  };

  // Page data from JSON
  headerData: any = null;
  pageTitleData: any = null;
  formSectionData: any = null;
  footerData: any = null;

  constructor(
    private logger: LoggerService,
    private packageService: PackageService,
    private messageService: NotificationService
  ) {}

  ngOnInit(): void {
    this.loadPageContent();
    this.loadPackages();
  }

  /**
   * Angular OnChanges handler that reacts to changes in pageData, pages, currentPageType, and elements to load content and update navigation.
   * @example
   * gOnChanges({ pageData: { firstChange: false }, elements: { firstChange: false }, pages: null, currentPageType: null })
   * void
   * @param {{SimpleChanges}} {{changes}} - Change object provided by Angular containing previous and current values for inputs.
   * @returns {{void}} Return nothing.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageData'] && !changes['pageData'].firstChange) {
      this.loadPageContent();
    }
    // Update navigation when pages or currentPageType changes
    if ((changes['pages'] || changes['currentPageType']) && this.headerData) {
      this.updateHeaderNavigation();
    }
    // Reload page content when elements change (for background color updates)
    if (changes['elements'] && !changes['elements'].firstChange) {
      // Force reload of pageTitleData and formSectionData to get updated background color
      const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
      const sections = page?.sections || {};
      this.pageTitleData = sections['page-title'] || {};
      this.formSectionData = sections.form_section || {};
    }
  }

  /**
   * Load and initialize Fleet Management page sections from this.pageData into component properties and refresh header navigation.
   * @example
   * loadPageContent()
   * undefined
   * @returns {void} No return value; updates component state (headerData, pageTitleData, formSectionData, footerData) and calls updateHeaderNavigation().
   **/
  private loadPageContent(): void {
    if (!this.pageData) return;

    this.logger.debug('?? Loading Fleet Management page content:', this.pageData);

    // Extract sections from page data - handle both editor and preview formats
    // Editor format: pageData.page.sections
    // Preview format: pageData.json_data.page.sections
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    const sections = page?.sections || {};

    this.headerData = sections.header || {};
    this.pageTitleData = sections['page-title'] || {};
    this.formSectionData = sections.form_section || {};
    this.footerData = sections.footer || {};

    this.logger.debug('Fleet page sections loaded:', {
      header: this.headerData,
      hero: this.pageTitleData,
      form: this.formSectionData,
      footer: this.footerData,
    });

    // Update header navigation to match current pages
    this.updateHeaderNavigation();
  }

  /**
   * Update the component's headerData.navigation from this.pages and ensure a default CTA button exists.
   * @example
   * updateHeaderNavigation()
   * undefined
   * @returns {void} Updates this.headerData in-place; no return value.
   **/
  private updateHeaderNavigation(): void {
    if (!this.headerData) this.headerData = {};

    // Generate navigation from pages if pages are provided
    // Filter out inactive pages (visible: false means active: false)
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      this.headerData.navigation = activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        active: page.pageType === this.currentPageType,
      }));
      this.logger.debug(
        'Updated header navigation for Fleet Management:',
        this.headerData.navigation
      );
    }

    // Ensure CTA button exists
    if (!this.headerData.ctaButton && !this.headerData.button) {
      this.headerData.ctaButton = {
        text: '',
        link: '#booking',
        backgroundColor: '#1B5BFF',
        textColor: '#FFFFFF',
      };
    }
  }

  /**
   * Load car wash packages from the package service and update component state (this.packages and this.loading).
   * @example
   * loadPackages()
   * undefined
   * @returns {{void}} No return value; updates component state (this.packages and this.loading).
   **/
  private loadPackages(): void {
    this.loading = true;

    this.packageService.getCarWashPackages().subscribe({
      next: response => {
        this.logger.debug('? Packages loaded for fleet form:', response);
        if (response.status && response.data) {
          this.packages = response.data;
        }
        this.loading = false;
      },
      error: err => {
        this.logger.error('? Error loading packages:', err);
        this.loading = false;
      },
    });
  }

  onSubmit(): void {
    // Check if we're in website preview mode
    if (this.isWebsitePreview()) {
      this.messageService.add({
        severity: 'info',
        summary: 'Preview Mode',
        detail: 'Form submission is only available on the published website.',
      });
      return;
    }

    this.messageService.add({
      severity: 'info',
      summary: 'Form Submitted',
      detail:
        'Form submission would be sent to: ' +
        (this.formSectionData?.api?.submit_url || '/api/fleet'),
    });
  }

  /**
   * Check if we're in website preview mode (URL contains /preview?websiteId=)
   */
  private isWebsitePreview(): boolean {
    return (
      window.location.href.includes('/preview?websiteId=') ||
      window.location.href.includes('/preview?websiteld=')
    );
  }

  /**
   * Check if a section should be visible based on section visibility settings
   */
  isSectionVisible(sectionKey: string): boolean {
    if (!this.sectionVisibility || Object.keys(this.sectionVisibility).length === 0) {
      return true;
    }
    return this.sectionVisibility[sectionKey] !== false;
  }

  // Header data helpers for shared component
  /**
   * Returns the header logo object from the component's header content, using defaults if none are provided.
   * @example
   * etHeaderLogo()
   * { image: 'https://example.com/logo.png', text: 'Logo', link: '/' }
   * @returns {Logo} The logo object containing image, text and link properties.
   */
  getHeaderLogo(): Logo {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.logo) {
      return {
        image: headerContent.logo.image || headerContent.logo.url || '',
        text: headerContent.logo.text || 'Logo',
        link: headerContent.logo.link || '/',
      };
    }
    return { text: 'Logo', link: '/' };
  }

  /**
   * Returns the header navigation items for the preview, using header content if present or generating from visible pages.
   * @example
   * etHeaderNavigation()
   * [
   *   { label: "Overview", link: "#overview", active: true },
   *   { label: "Features", link: "#features", active: false },
   *   { label: "Pricing", link: "#pricing", active: false }
   * ]
   * @returns {{NavigationItem[]}} Array of NavigationItem objects representing the header navigation.
   **/
  getHeaderNavigation(): NavigationItem[] {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.navigation && Array.isArray(headerContent.navigation)) {
      return headerContent.navigation;
    }
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      return activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        active: page.pageType === this.currentPageType,
      }));
    }
    return [];
  }

  /**
   * Returns the header CTA button configuration from the component's header content, falling back to sensible defaults.
   * @example
   * etHeaderCTAButton()
   * { text: 'Book Wash', link: '#', backgroundColor: '#1B5BFF', textColor: '#FFFFFF' }
   * @param {void} none - No arguments.
   * @returns {CTAButton|null} CTAButton object with text, link, backgroundColor and textColor, or null.
   */
  getHeaderCTAButton(): CTAButton | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    return buildHeaderCtaButton(headerContent?.ctaButton);
  }

  getHeaderBackgroundColor(): string | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.backgroundColor) {
      return headerContent.backgroundColor as string;
    }
    return null;
  }

  /**
   * Build footer quick links for the preview: returns configured quick links if present, otherwise derives links from visible pages.
   * @example
   * etFooterQuickLinks()
   * [{ label: 'Home', link: '#home' }, { label: 'About Us', link: '#about-us' }]
   * @returns {Array<{label: string, link: string}>} Array of link objects with label and link properties.
   */
  getFooterQuickLinks(): { label: string; link: string }[] {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const storedLinks = Array.isArray(footerContent?.quickLinks) ? footerContent.quickLinks : [];
    return resolveFooterQuickLinks(storedLinks, this.pages);
  }

  /**
   * Retrieve and normalize social links from the footer content, returning each as an object with platform, url, and icon.
   * @example
   * etFooterSocialLinks()
   * [{ platform: 'Twitter', url: 'https://twitter.com/example', icon: 'twitter' }]
   * @returns {Array<{ platform: string; url: string; icon: string }>} Array of normalized social link objects.
   **/
  getFooterSocialLinks(): { platform: string; url: string; icon: string }[] {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.socialLinks && Array.isArray(footerContent.socialLinks)) {
      return footerContent.socialLinks.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }
    return [];
  }

  getFooterSocialLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    return footerContent?.socialLinksTitle || 'Connect with us';
  }

  getFooterQuickLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    return footerContent?.quickLinksTitle || 'Quick links';
  }

  getFooterCopyright(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.copyright) {
      return footerContent.copyright;
    }
    return `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`;
  }

  /**
   * Get footer logo data from the footer element or fall back to header logo or a default.
   * @example
   * etFooterLogo()
   * { text: 'way', image: '' }
   * @param {{void}} {{}} - This function accepts no arguments.
   * @returns {{ text?: string; image?: string } | undefined} Returns an object with optional text and image properties, or undefined if unavailable.
   **/
  getFooterLogo(): { text?: string; image?: string } | undefined {
    // Get logo from footer element content
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;

    if (footerContent?.logo) {
      const logo = footerContent.logo;
      return {
        text: logo.text || 'way',
        image: logo.image || logo.url || '',
      };
    }

    // Fallback to header logo if footer logo not available
    const headerLogo = this.getHeaderLogo();
    if (headerLogo && headerLogo.image) {
      return {
        text: headerLogo.text,
        image: headerLogo.image,
      };
    }
    return { text: 'way' };
  }

  getFooterBackgroundColor(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.backgroundColor) {
      return footerContent.backgroundColor;
    }
    return '#000920';
  }

  getHeaderElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'header');
  }

  getFooterElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'footer');
  }

  onHeaderClick(): void {
    const headerElement = this.getHeaderElement();
    if (headerElement) {
      this.onElementClick(headerElement);
    }
  }

  onElementClick(element: WebsiteElement): void {
    if (!element) return;
    this.elementSelect.emit(element);
  }

  onFooterClick(event: Event): void {
    event.stopPropagation();
    const footerElement = this.getFooterElement();
    if (footerElement) {
      if (!footerElement.type || footerElement.type !== 'footer') {
        footerElement.type = 'footer';
      }
      if (!footerElement.id || footerElement.id !== 'footer') {
        footerElement.id = 'footer';
      }
      this.onElementClick(footerElement);
    }
  }

  onNavigationClick(pageType: string): void {
    this.navigationClick.emit(pageType);
  }

  /**
   * Get primary color from theme or use default
   */
  getPrimaryColor(): string {
    return this.primaryColor || '#1B5BFF';
  }

  /**
   * Get heading color from branding or use default
   */
  getHeadingColor(): string {
    return this.headingColor || '#1E3A8A';
  }

  /**
   * Get paragraph color from branding or use default
   */
  getParagraphColor(): string {
    return this.paragraphColor || '#64748B';
  }

  /**
   * Get heading font from branding or use default
   */
  getHeadingFont(): string {
    return this.headingFont || 'Inter';
  }

  /**
   * Get body font from branding or use default
   */
  getBodyFont(): string {
    return this.bodyFont || 'Inter';
  }

  /**
   * Get hero title text
   */
  getPageTitle(): string {
    if (this.pageTitleData?.title) {
      if (typeof this.pageTitleData.title === 'string') {
        return this.pageTitleData.title;
      } else if (this.pageTitleData.title?.label) {
        return this.pageTitleData.title.label;
      }
    }
    return 'Fleet Management';
  }

  /**
   * Get hero subtitle/description text
   */
  getPageSubtitle(): string {
    if (this.pageTitleData?.description) {
      return this.pageTitleData.description;
    } else if (this.pageTitleData?.subtitle) {
      return this.pageTitleData.subtitle;
    }
    return 'Keep your business vehicles spotless and road-ready with our tailored fleet wash plans. Enjoy convenient scheduling, consistent quality, and cost savings designed for companies of any size.';
  }

  /**
   * Get title styles for ngStyle binding
   */
  getTitleStyles(): any {
    // Check page-level title first (highest priority)
    const page = this.pageData?.page || this.pageData;
    if (page?.title && typeof page.title === 'object' && page.title.style?.text_style) {
      const titleStyleObj = page.title.style.text_style;
      const styles: any = {};
      if (titleStyleObj.fontWeight) styles['font-weight'] = titleStyleObj.fontWeight;
      if (titleStyleObj.fontStyle) styles['font-style'] = titleStyleObj.fontStyle;
      if (titleStyleObj.textDecoration) styles['text-decoration'] = titleStyleObj.textDecoration;
      if (titleStyleObj.textAlign) styles['text-align'] = titleStyleObj.textAlign;
      return styles;
    }

    // Fallback to section-level title
    if (this.pageTitleData?.title && typeof this.pageTitleData.title === 'object') {
      const title = this.pageTitleData.title;
      const titleStyleObj = title.style?.text_style;

      if (titleStyleObj && typeof titleStyleObj === 'object') {
        const styles: any = {};
        if (titleStyleObj.fontWeight) styles['font-weight'] = titleStyleObj.fontWeight;
        if (titleStyleObj.fontStyle) styles['font-style'] = titleStyleObj.fontStyle;
        if (titleStyleObj.textDecoration) styles['text-decoration'] = titleStyleObj.textDecoration;
        if (titleStyleObj.textAlign) styles['text-align'] = titleStyleObj.textAlign;
        return styles;
      }
    }

    return {};
  }

  /**
   * Get form theme
   */
  getFormTheme(): 'light' | 'dark' {
    return this.formSectionData?.theme || this.formSectionData?.formTheme || 'light';
  }

  /**
   * Get button text
   */
  getButtonText(): string {
    return (
      this.formSectionData?.buttonText || this.formSectionData?.button?.text || 'Book Appointment'
    );
  }

  /**
   * Get fleet section element for selection
   */
  getFleetSectionElement(): WebsiteElement | undefined {
    return this.elements.find(
      el =>
        el.type === 'fleets' ||
        el.id === 'fleets' ||
        (el as any).originalKey === 'page-title' ||
        (el as any).originalKey === 'form_section'
    );
  }

  /**
   * Handle hero section click
   */
  /**
   * Get page header section background color
   */
  getPageHeaderSectionBackgroundColor(): string | null {
    // Priority 1: Check element content (most up-to-date, updated immediately)
    const fleetElement = this.getFleetSectionElement();
    const fleetContent = fleetElement?.content as any;
    if (fleetContent?.backgroundColor) {
      return fleetContent.backgroundColor;
    }
    // Priority 2: Check pageTitleData (from API/pageData)
    if (this.pageTitleData?.backgroundColor) {
      return this.pageTitleData.backgroundColor;
    }
    // Priority 3: Check formSectionData (from API/pageData)
    if (this.formSectionData?.backgroundColor) {
      return this.formSectionData.backgroundColor;
    }
    // Priority 4: Check page-level theme background color
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    if (page?.theme?.background_color) {
      return page.theme.background_color;
    }
    // Priority 5: Check pageData directly
    const sections = page?.sections || {};
    const pageTitleSection = sections['page-title'] || {};
    if (pageTitleSection.backgroundColor) {
      return pageTitleSection.backgroundColor;
    }
    const formSection = sections.form_section || {};
    if (formSection.backgroundColor) {
      return formSection.backgroundColor;
    }
    // Default fallback
    return null;
  }

  /**
   * Get form section background color (same as page header for unified look)
   */
  getFormSectionBackgroundColor(): string | null {
    return this.getPageHeaderSectionBackgroundColor();
  }

  /**
   * Handle clicks on the page header section: stops event propagation and emits a fleet element built from the current page header data.
   * @example
   * nPageHeaderSectionClick(new Event('click'))
   * undefined
   * @param {Event} event - Click event from the page header section; propagation is stopped and a fleet element is emitted.
   * @returns {void} Does not return a value; emits a WebsiteElement via this.elementSelect.
   **/
  onPageHeaderSectionClick(event: Event): void {
    event.stopPropagation();
    const fleetElement: WebsiteElement = {
      id: 'fleets',
      type: 'fleets',
      content: {
        ...(this.pageTitleData || {}),
        title: this.getPageTitle(),
        subtitle: this.getPageSubtitle(),
        formTheme: this.getFormTheme(),
        buttonText: this.getButtonText(),
        backgroundColor: this.getPageHeaderSectionBackgroundColor(),
      },
      selected: true,
      originalKey: 'page-title',
    } as any;
    this.logger.debug('🚛 Hero section clicked, emitting element:', fleetElement);
    this.elementSelect.emit(fleetElement);
  }

  /**
   * Handle form section click
   */
  onFormSectionClick(event: Event): void {
    event.stopPropagation();
    const fleetElement: WebsiteElement = {
      id: 'fleets',
      type: 'fleets',
      content: {
        ...(this.pageTitleData || {}),
        title: this.getPageTitle(),
        subtitle: this.getPageSubtitle(),
        formTheme: this.getFormTheme(),
        buttonText: this.getButtonText(),
        backgroundColor: this.getFormSectionBackgroundColor(),
      },
      selected: true,
      originalKey: 'form_section',
    } as any;
    this.logger.debug('🚛 Form section clicked, emitting element:', fleetElement);
    this.elementSelect.emit(fleetElement);
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
